import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PlusCircle, Trash2, CalendarDays, MapPin, Users, Euro } from "lucide-react";
import { toast } from "sonner";

const emptyPatient = () => ({
  patient_id: "",
  nom: "",
  prenom: "",
  adresse: "",
  telephone: "",
  email: "",
  numero_national: "",
  forfait: "",
  montant_estime: "",
  passages: [],
  notes: "",
  mode: "existing",
});

const emptyCreneaux = {
  matin: { selected: false, heure_debut: "06:30", heure_fin: "12:30" },
  midi: { selected: false, heure_debut: "12:00", heure_fin: "14:00" },
  soir: { selected: false, heure_debut: "16:00", heure_fin: "20:00" },
};

export default function NouvelleDemandeRemplacement() {
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();

  const [existingPatients, setExistingPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    date: "",
    zone: "",
    creneaux: emptyCreneaux,
    patients: [emptyPatient()],
    notes_generales: "",
  });

  const fetchPatients = async () => {
    try {
      const res = await axiosInstance.get("/remplacements-domicile/patients/me");
      setExistingPatients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setExistingPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  const selectedPeriods = useMemo(
    () =>
      Object.entries(form.creneaux)
        .filter(([, value]) => value.selected)
        .map(([key]) => key),
    [form.creneaux]
  );

  const totalEstimated = useMemo(
    () =>
      form.patients.reduce(
        (sum, patient) => sum + (Number(patient.montant_estime) || 0),
        0
      ),
    [form.patients]
  );

  const togglePeriod = (period) => {
    setForm((prev) => ({
      ...prev,
      creneaux: {
        ...prev.creneaux,
        [period]: {
          ...prev.creneaux[period],
          selected: !prev.creneaux[period].selected,
        },
      },
      patients: prev.patients.map((patient) => ({
        ...patient,
        passages: patient.passages.filter((value) => {
          if (value !== period) return true;
          return prev.creneaux[period].selected;
        }),
      })),
    }));
  };

  const updatePeriod = (period, field, value) => {
    setForm((prev) => ({
      ...prev,
      creneaux: {
        ...prev.creneaux,
        [period]: {
          ...prev.creneaux[period],
          [field]: value,
        },
      },
    }));
  };

  const updatePatient = (index, field, value) => {
    setForm((prev) => {
      const patients = [...prev.patients];
      patients[index] = { ...patients[index], [field]: value };
      return { ...prev, patients };
    });
  };

  const togglePatientPassage = (index, passage) => {
    if (!form.creneaux[passage]?.selected) {
      toast.error(`Activez d'abord le créneau ${passage}.`);
      return;
    }

    setForm((prev) => {
      const patients = [...prev.patients];
      const current = patients[index].passages || [];
      const selected = current.includes(passage);

      patients[index] = {
        ...patients[index],
        passages: selected
          ? current.filter((item) => item !== passage)
          : [...current, passage],
      };

      return { ...prev, patients };
    });
  };

  const addPatient = () => {
    setForm((prev) => ({
      ...prev,
      patients: [...prev.patients, emptyPatient()],
    }));
  };

  const removePatient = (index) => {
    setForm((prev) => ({
      ...prev,
      patients:
        prev.patients.length === 1
          ? prev.patients
          : prev.patients.filter((_, i) => i !== index),
    }));
  };

  const chooseExistingPatient = (index, patientId) => {
    const patient = existingPatients.find(
      (item) => String(item._id) === String(patientId)
    );

    setForm((prev) => {
      const patients = [...prev.patients];

      patients[index] = {
        ...patients[index],
        patient_id: patientId,
        nom: patient?.nom || "",
        prenom: patient?.prenom || "",
        adresse: patient?.adresse || "",
        telephone: patient?.telephone || "",
        email: patient?.email || "",
        numero_national: patient?.numero_national || "",
      };

      return { ...prev, patients };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.date) {
      toast.error("Choisissez la date de la tournée.");
      return;
    }

    if (!form.zone.trim()) {
      toast.error("Indiquez la zone ou la commune.");
      return;
    }

    if (selectedPeriods.length === 0) {
      toast.error("Sélectionnez au moins Matin, Midi ou Soir.");
      return;
    }

    const invalidPatient = form.patients.find((patient) => {
      if (!patient.patient_id && (!patient.nom.trim() || !patient.prenom.trim())) {
        return true;
      }

      return !Array.isArray(patient.passages) || patient.passages.length === 0;
    });

    if (invalidPatient) {
      toast.error(
        "Chaque patient doit être identifié et avoir au moins un passage."
      );
      return;
    }

    const payload = {
      date: form.date,
      zone: form.zone.trim(),
      creneaux: form.creneaux,
      patients: form.patients.map((patient) => ({
        patient_id: patient.patient_id || null,
        nom: patient.patient_id ? null : patient.nom.trim(),
        prenom: patient.patient_id ? null : patient.prenom.trim(),
        adresse: patient.patient_id ? null : patient.adresse.trim(),
        telephone: patient.patient_id ? null : patient.telephone.trim(),
        email: patient.patient_id ? null : patient.email.trim(),
        numero_national: patient.patient_id ? null : patient.numero_national.trim(),
        forfait: patient.forfait.trim() || null,
        montant_estime: Number(patient.montant_estime) || 0,
        passages: patient.passages,
        notes: patient.notes.trim() || null,
      })),
      notes_generales: form.notes_generales.trim() || null,
    };

    try {
      setSaving(true);

      await axiosInstance.post("/remplacements-domicile", payload);

      toast.success("Votre demande a été envoyée à Sana Care.");
      navigate("/remplacements-domicile");
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail;

      toast.error(
        Array.isArray(detail)
          ? detail.map((item) => item.msg).join(" | ")
          : detail || "Impossible d'envoyer la demande."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title="Nouvelle demande de remplacement">
      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="border-[#d4ebeb] bg-[#f4fafa]">
          <CardHeader>
            <CardTitle className="text-[#315f63]">
              1. Informations générales
            </CardTitle>
          </CardHeader>

          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label>Date de la tournée *</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, date: e.target.value }))
                }
                required
              />
            </div>

            <div className="space-y-1">
              <Label>Zone / commune *</Label>
              <Input
                placeholder="Ex : La Louvière"
                value={form.zone}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, zone: e.target.value }))
                }
                required
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#d4ebeb] bg-white">
          <CardHeader>
            <CardTitle className="text-[#315f63]">
              2. Périodes à remplacer
            </CardTitle>
          </CardHeader>

          <CardContent className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {[
              { key: "matin", label: "🌅 Matin" },
              { key: "midi", label: "☀️ Midi" },
              { key: "soir", label: "🌙 Soir" },
            ].map(({ key, label }) => {
              const item = form.creneaux[key];

              return (
                <div
                  key={key}
                  className={`rounded-xl border p-4 ${
                    item.selected
                      ? "border-[#6faeb2] bg-[#e6f3f3]"
                      : "border-[#d4ebeb] bg-white"
                  }`}
                >
                  <label className="flex cursor-pointer items-center gap-3 font-semibold text-[#315f63]">
                    <input
                      type="checkbox"
                      checked={item.selected}
                      onChange={() => togglePeriod(key)}
                      className="h-4 w-4 accent-[#6faeb2]"
                    />
                    {label}
                  </label>

                  {item.selected && (
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <div>
                        <Label>Début</Label>
                        <Input
                          type="time"
                          value={item.heure_debut}
                          onChange={(e) =>
                            updatePeriod(key, "heure_debut", e.target.value)
                          }
                        />
                      </div>

                      <div>
                        <Label>Fin</Label>
                        <Input
                          type="time"
                          value={item.heure_fin}
                          onChange={(e) =>
                            updatePeriod(key, "heure_fin", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="border-[#d4ebeb] bg-[#f4fafa]">
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-[#315f63]">
                3. Patients de la tournée
              </CardTitle>

              <Button
                type="button"
                onClick={addPatient}
                className="bg-[#6faeb2] hover:bg-[#5b979b] text-white"
              >
                <PlusCircle size={16} className="mr-2" />
                Ajouter un patient
              </Button>
            </div>
          </CardHeader>

          <CardContent className="space-y-5">
            {form.patients.map((patient, index) => (
              <div
                key={index}
                className="rounded-xl border border-[#d4ebeb] bg-white p-5"
              >
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-bold text-[#315f63]">
                    Patient {index + 1}
                  </h3>

                  {form.patients.length > 1 && (
                    <Button
                      type="button"
                      onClick={() => removePatient(index)}
                      variant="outline"
                      className="border-red-200 text-red-600"
                    >
                      <Trash2 size={15} className="mr-2" />
                      Retirer
                    </Button>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="space-y-1">
                    <Label>Patient existant</Label>

                    <select
                      value={patient.patient_id}
                      onChange={(e) =>
                        chooseExistingPatient(index, e.target.value)
                      }
                      className="w-full rounded-md border border-[#d4ebeb] bg-white px-3 py-2"
                    >
                      <option value="">
                        — Nouveau patient —
                      </option>

                      {existingPatients.map((item) => (
                        <option key={item._id} value={item._id}>
                          {item.prenom} {item.nom}
                        </option>
                      ))}
                    </select>

                    {loadingPatients && (
                      <p className="text-xs text-gray-500">
                        Chargement des patients existants…
                      </p>
                    )}
                  </div>

                  {!patient.patient_id && (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <Label>Nom *</Label>
                        <Input
                          value={patient.nom}
                          onChange={(e) =>
                            updatePatient(index, "nom", e.target.value)
                          }
                        />
                      </div>

                      <div>
                        <Label>Prénom *</Label>
                        <Input
                          value={patient.prenom}
                          onChange={(e) =>
                            updatePatient(index, "prenom", e.target.value)
                          }
                        />
                      </div>

                      <div>
                        <Label>Adresse</Label>
                        <Input
                          value={patient.adresse}
                          onChange={(e) =>
                            updatePatient(index, "adresse", e.target.value)
                          }
                        />
                      </div>

                      <div>
                        <Label>Téléphone</Label>
                        <Input
                          value={patient.telephone}
                          onChange={(e) =>
                            updatePatient(index, "telephone", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                      <Label>Forfait / catégorie</Label>
                      <Input
                        placeholder="Ex : Forfait A"
                        value={patient.forfait}
                        onChange={(e) =>
                          updatePatient(index, "forfait", e.target.value)
                        }
                      />
                    </div>

                    <div>
                      <Label>Montant estimé mutuelle (€)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={patient.montant_estime}
                        onChange={(e) =>
                          updatePatient(index, "montant_estime", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Passage(s)</Label>

                    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                      {[
                        { key: "matin", label: "🌅 Matin" },
                        { key: "midi", label: "☀️ Midi" },
                        { key: "soir", label: "🌙 Soir" },
                      ].map(({ key, label }) => {
                        const available = form.creneaux[key]?.selected;
                        const selected = patient.passages.includes(key);

                        return (
                          <label
                            key={key}
                            className={`flex cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-3 ${
                              selected
                                ? "border-green-500 bg-green-100 text-green-800"
                                : available
                                  ? "border-gray-200 bg-white text-gray-700"
                                  : "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
                            }`}
                          >
                            <input
                              type="checkbox"
                              disabled={!available}
                              checked={selected}
                              onChange={() => togglePatientPassage(index, key)}
                            />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <Label>Note facultative</Label>
                    <Input
                      placeholder="Informations utiles pour ce patient"
                      value={patient.notes}
                      onChange={(e) =>
                        updatePatient(index, "notes", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-[#d4ebeb] bg-white">
          <CardHeader>
            <CardTitle className="text-[#315f63]">
              4. Récapitulatif
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-lg bg-[#f4fafa] p-4">
                <p className="text-xs text-gray-500">Patients</p>
                <p className="text-2xl font-bold text-[#315f63]">
                  {form.patients.length}
                </p>
              </div>

              <div className="rounded-lg bg-[#f4fafa] p-4">
                <p className="text-xs text-gray-500">Périodes</p>
                <p className="font-semibold text-[#315f63]">
                  {selectedPeriods.length
                    ? selectedPeriods
                        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
                        .join(" + ")
                    : "Aucune"}
                </p>
              </div>

              <div className="rounded-lg bg-[#f4fafa] p-4">
                <p className="text-xs text-gray-500">Estimation mutuelle</p>
                <p className="text-2xl font-bold text-[#315f63]">
                  {totalEstimated.toFixed(2)} €
                </p>
              </div>
            </div>

            <div>
              <Label>Remarque générale</Label>
              <Input
                placeholder="Informations générales sur la tournée"
                value={form.notes_generales}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    notes_generales: e.target.value,
                  }))
                }
              />
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/remplacements-domicile")}
                className="border-[#b8dede] text-[#315f63]"
              >
                Annuler
              </Button>

              <Button
                type="submit"
                disabled={saving}
                className="bg-[#6faeb2] hover:bg-[#5b979b] text-white"
              >
                {saving ? "Envoi..." : "Envoyer la demande à Sana Care"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </Layout>
  );
}
